import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Calendar, Clock, Video, Phone, Mail, MapPin, Plus,
  Loader2, CheckCircle, XCircle, Copy, Trash2, Pencil,
  ExternalLink, Link2, Users, ToggleLeft, ToggleRight,
} from 'lucide-react';
import api from '@/lib/api';

const CANAUX_MAP = {
  visio: { icon: Video, label: 'Visio', color: 'bg-blue-100 text-blue-700' },
  telephone: { icon: Phone, label: 'Tél.', color: 'bg-emerald-100 text-emerald-700' },
  email: { icon: Mail, label: 'Email', color: 'bg-purple-100 text-purple-700' },
  presentiel: { icon: MapPin, label: 'Présentiel', color: 'bg-amber-100 text-amber-700' },
};

const STATUT_RDV = {
  confirme: { label: 'Confirmé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: XCircle },
  reporte: { label: 'Reporté', color: 'bg-amber-100 text-amber-700', icon: Clock },
  termine: { label: 'Terminé', color: 'bg-gray-100 text-gray-700', icon: CheckCircle },
};

export default function BookingManagementTab() {
  const [liens, setLiens] = useState([]);
  const [rdvList, setRdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewLink, setShowNewLink] = useState(false);
  const [showEditRdv, setShowEditRdv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [view, setView] = useState('rdv'); // 'rdv' or 'liens'
  const [filterStatut, setFilterStatut] = useState('tous');

  const [newLink, setNewLink] = useState({
    titre: 'Prise de RDV Bureau Agricole',
    description: '',
    duree_defaut: 30,
    canaux: ['visio', 'telephone', 'presentiel'],
    jours_avance: 30,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [liensRes, rdvRes] = await Promise.all([
        api.get('/booking/liens'),
        api.get('/booking/rdv'),
      ]);
      setLiens(liensRes.data.liens || []);
      setRdvList(rdvRes.data.rdv || []);
    } catch (err) {
      toast.error('Erreur chargement des données booking');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateLink = async () => {
    setSaving(true);
    try {
      const res = await api.post('/booking/creer-lien', newLink);
      if (res.data.success) {
        toast.success('Lien de booking créé !');
        setShowNewLink(false);
        fetchData();
      }
    } catch (err) {
      toast.error('Erreur création du lien');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = (linkId) => {
    const url = `${window.location.origin}/booking/${linkId}`;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const handleToggleLink = async (linkId, actif) => {
    try {
      await api.patch(`/booking/liens/${linkId}`, { actif: !actif });
      toast.success(actif ? 'Lien désactivé' : 'Lien activé');
      fetchData();
    } catch (err) {
      toast.error('Erreur modification du lien');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Supprimer ce lien et tous ses RDV associés ?')) return;
    setDeletingId(linkId);
    try {
      await api.delete(`/booking/liens/${linkId}`);
      toast.success('Lien supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteRdv = async (rdvId) => {
    if (!window.confirm('Supprimer ce RDV ? L\'événement Google Calendar sera aussi annulé.')) return;
    setDeletingId(rdvId);
    try {
      await api.delete(`/booking/rdv/${rdvId}`);
      toast.success('RDV supprimé et annulé du calendrier');
      fetchData();
    } catch (err) {
      toast.error('Erreur suppression du RDV');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateRdv = async () => {
    if (!showEditRdv) return;
    setSaving(true);
    try {
      const { id, ...fields } = showEditRdv;
      await api.patch(`/booking/rdv/${id}`, fields);
      toast.success('RDV modifié');
      setShowEditRdv(null);
      fetchData();
    } catch (err) {
      toast.error('Erreur modification du RDV');
    } finally {
      setSaving(false);
    }
  };

  const filteredRdv = rdvList.filter(r =>
    filterStatut === 'tous' || r.statut === filterStatut
  );

  const upcomingCount = rdvList.filter(r => r.statut === 'confirme' && r.date >= new Date().toISOString().split('T')[0]).length;

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="booking-management-tab">
      {/* Header stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{upcomingCount}</p>
            <p className="text-xs text-emerald-600">RDV à venir</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{rdvList.length}</p>
            <p className="text-xs text-blue-600">Total RDV</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{liens.length}</p>
            <p className="text-xs text-purple-600">Liens actifs</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{rdvList.filter(r => r.statut === 'annule').length}</p>
            <p className="text-xs text-amber-600">Annulés</p>
          </CardContent>
        </Card>
      </div>

      {/* Toggle view + actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={view === 'rdv' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('rdv')}
            data-testid="btn-view-rdv"
          >
            <Calendar className="w-4 h-4 mr-1" />Mes RDV ({rdvList.length})
          </Button>
          <Button
            variant={view === 'liens' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('liens')}
            data-testid="btn-view-liens"
          >
            <Link2 className="w-4 h-4 mr-1" />Liens de booking ({liens.length})
          </Button>
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowNewLink(true)} data-testid="btn-new-booking-link">
          <Plus className="w-4 h-4 mr-1" />Nouveau lien
        </Button>
      </div>

      {/* ===== VUE RDV ===== */}
      {view === 'rdv' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-48" data-testid="filter-rdv-statut"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                {Object.entries(STATUT_RDV).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filteredRdv.length === 0 ? (
            <Card><CardContent className="py-10 text-center">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="font-medium">Aucun rendez-vous</p>
              <p className="text-sm text-muted-foreground mt-1">Les RDV pris via vos liens de booking apparaîtront ici</p>
            </CardContent></Card>
          ) : (
            filteredRdv.map(rdv => {
              const canal = CANAUX_MAP[rdv.canal] || CANAUX_MAP.visio;
              const CanalIcon = canal.icon;
              const statut = STATUT_RDV[rdv.statut] || STATUT_RDV.confirme;
              const StatutIcon = statut.icon;
              const isPast = rdv.date < new Date().toISOString().split('T')[0];
              return (
                <Card key={rdv.id} className={`transition-shadow hover:shadow-md ${isPast ? 'opacity-70' : ''}`} data-testid={`rdv-card-${rdv.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{rdv.nom}</h3>
                          <Badge className={statut.color}>
                            <StatutIcon className="w-3 h-3 mr-1" />{statut.label}
                          </Badge>
                          <Badge className={canal.color}>
                            <CanalIcon className="w-3 h-3 mr-1" />{canal.label}
                          </Badge>
                          {isPast && <Badge variant="outline" className="text-gray-500">Passé</Badge>}
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-sm mt-2">
                          <div>
                            <p className="text-muted-foreground text-xs">Date</p>
                            <p className="font-medium">{new Date(rdv.date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Heure</p>
                            <p className="font-medium">{rdv.heure} ({rdv.duree_minutes || 30} min)</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Email</p>
                            <p className="truncate">{rdv.email}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Entreprise</p>
                            <p>{rdv.entreprise || '-'}</p>
                          </div>
                        </div>
                        {rdv.message && <p className="text-xs text-muted-foreground mt-1 italic">"{rdv.message}"</p>}
                        {rdv.meet_link && (
                          <a href={rdv.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1">
                            <Video className="w-3 h-3" />Lien Google Meet
                          </a>
                        )}
                      </div>
                      <div className="flex gap-1 ml-3">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setShowEditRdv({
                            id: rdv.id,
                            date: rdv.date,
                            heure: rdv.heure,
                            canal: rdv.canal,
                            statut: rdv.statut,
                            duree_minutes: rdv.duree_minutes || 30,
                            notes_internes: rdv.notes_internes || '',
                          })}
                          data-testid={`btn-edit-rdv-${rdv.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteRdv(rdv.id)}
                          disabled={deletingId === rdv.id}
                          data-testid={`btn-delete-rdv-${rdv.id}`}
                        >
                          {deletingId === rdv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* ===== VUE LIENS ===== */}
      {view === 'liens' && (
        <div className="space-y-3">
          {liens.length === 0 ? (
            <Card><CardContent className="py-10 text-center">
              <Link2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="font-medium">Aucun lien de booking</p>
              <p className="text-sm text-muted-foreground mt-1">Créez un lien pour partager vos disponibilités</p>
            </CardContent></Card>
          ) : (
            liens.map(lien => (
              <Card key={lien.id} className={`transition-shadow hover:shadow-md ${!lien.actif ? 'opacity-60' : ''}`} data-testid={`booking-link-${lien.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{lien.titre}</h3>
                        <Badge variant={lien.actif ? 'default' : 'secondary'} className={lien.actif ? 'bg-green-100 text-green-700' : ''}>
                          {lien.actif ? 'Actif' : 'Désactivé'}
                        </Badge>
                        <Badge variant="outline">{lien.nb_rdv_pris || 0} RDV pris</Badge>
                      </div>
                      {lien.description && <p className="text-sm text-muted-foreground">{lien.description}</p>}
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground">Durée: {lien.duree_defaut} min</span>
                        <span className="text-muted-foreground">|</span>
                        <div className="flex gap-1">
                          {(lien.canaux || []).map(c => {
                            const cn = CANAUX_MAP[c];
                            return cn ? <Badge key={c} variant="outline" className="text-xs py-0">{cn.label}</Badge> : null;
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 mt-1">
                        <code className="text-xs flex-1 text-gray-600">{window.location.origin}/booking/{lien.id}</code>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => handleCopyLink(lien.id)} data-testid={`copy-link-${lien.id}`}>
                          <Copy className="w-3.5 h-3.5 mr-1" />Copier
                        </Button>
                        <a href={`/booking/${lien.id}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost" className="h-7" data-testid={`open-link-${lien.id}`}>
                            <ExternalLink className="w-3.5 h-3.5 mr-1" />Ouvrir
                          </Button>
                        </a>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-3">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleToggleLink(lien.id, lien.actif)}
                        data-testid={`toggle-link-${lien.id}`}
                      >
                        {lien.actif ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteLink(lien.id)}
                        disabled={deletingId === lien.id}
                        data-testid={`delete-link-${lien.id}`}
                      >
                        {deletingId === lien.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ===== DIALOG NOUVEAU LIEN ===== */}
      <Dialog open={showNewLink} onOpenChange={setShowNewLink}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-emerald-600" />Nouveau lien de booking</DialogTitle>
            <DialogDescription>Créez un lien partageable pour vos rendez-vous</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Titre</Label>
              <Input value={newLink.titre} onChange={e => setNewLink(p => ({ ...p, titre: e.target.value }))} className="mt-1" data-testid="new-link-titre" />
            </div>
            <div>
              <Label>Description (optionnel)</Label>
              <Textarea value={newLink.description} onChange={e => setNewLink(p => ({ ...p, description: e.target.value }))} className="mt-1" rows={2} data-testid="new-link-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Durée par défaut (min)</Label>
                <Input type="number" value={newLink.duree_defaut} onChange={e => setNewLink(p => ({ ...p, duree_defaut: parseInt(e.target.value) || 30 }))} className="mt-1" />
              </div>
              <div>
                <Label>Jours d'avance</Label>
                <Input type="number" value={newLink.jours_avance} onChange={e => setNewLink(p => ({ ...p, jours_avance: parseInt(e.target.value) || 30 }))} className="mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLink(false)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleCreateLink} disabled={saving} data-testid="btn-confirm-new-link">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Créer le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG MODIFIER RDV ===== */}
      <Dialog open={!!showEditRdv} onOpenChange={() => setShowEditRdv(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-emerald-600" />Modifier le RDV</DialogTitle>
            <DialogDescription>Mettez à jour les détails. Le Google Calendar sera synchronisé.</DialogDescription>
          </DialogHeader>
          {showEditRdv && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={showEditRdv.date} onChange={e => setShowEditRdv(p => ({ ...p, date: e.target.value }))} className="mt-1" data-testid="edit-rdv-date" />
                </div>
                <div>
                  <Label>Heure</Label>
                  <Input type="time" value={showEditRdv.heure} onChange={e => setShowEditRdv(p => ({ ...p, heure: e.target.value }))} className="mt-1" data-testid="edit-rdv-heure" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Canal</Label>
                  <Select value={showEditRdv.canal} onValueChange={v => setShowEditRdv(p => ({ ...p, canal: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CANAUX_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={showEditRdv.statut} onValueChange={v => setShowEditRdv(p => ({ ...p, statut: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUT_RDV).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Durée (min)</Label>
                <Input type="number" value={showEditRdv.duree_minutes} onChange={e => setShowEditRdv(p => ({ ...p, duree_minutes: parseInt(e.target.value) || 30 }))} className="mt-1" />
              </div>
              <div>
                <Label>Notes internes</Label>
                <Textarea value={showEditRdv.notes_internes} onChange={e => setShowEditRdv(p => ({ ...p, notes_internes: e.target.value }))} className="mt-1" rows={2} placeholder="Notes privées..." data-testid="edit-rdv-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRdv(null)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleUpdateRdv} disabled={saving} data-testid="btn-confirm-edit-rdv">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
